import React, { useState } from 'react';
import { FiActivity, FiHelpCircle, FiTrendingDown, FiTrendingUp } from 'react-icons/fi';
import type { FunnelNarrative, FunnelStageScore } from '@troia-v3/types';

interface FunnelNarrativeCardProps {
  narrative: FunnelNarrative;
}

const formatNumber = (v: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(v);

const formatPct = (v: number | null): string => {
  if (v === null) return '—';
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(v)}%`;
};

/**
 * Cor semântica do score consolidado (única exceção à paleta roxa nesse card —
 * micro-pill de "nota geral", merece destaque).
 */
const consolidatedScoreColor = (score: number): string => {
  if (score >= 80) return '#10b981';
  if (score >= 60) return '#f59e0b';
  return '#ef4444';
};

const SCORE_FORMULA_LINES: string[] = [
  'Score consolidado = média das 4 etapas.',
  '',
  'Score por etapa (0-100):',
  '  • 60% — conversão atual / meta da etapa',
  '  • 20% — velocidade vs benchmark histórico (em construção)',
  '  • 20% — volume atual vs período anterior',
  '',
  'Metas iniciais:',
  '  • Visit → Lead: 7%',
  '  • Lead → Oportunidade: 30%',
  '  • Oportunidade → Ganho: 13%',
  '',
  'Cores: ≥ 80 verde · 60-79 amarelo · < 60 vermelho.',
];

/** Popover claro fixo (dropdown = tema claro sempre). */
const ScoreFormulaTooltip: React.FC = () => (
  <div
    className="absolute right-0 top-full mt-2 z-20 w-72 p-3 rounded-md shadow-lg text-xs"
    style={{
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      color: '#1e293b',
      lineHeight: 1.5,
    }}
    data-testid="funnel-score-formula-tooltip"
    role="tooltip"
  >
    <div className="font-semibold mb-1.5" style={{ color: '#0f172a' }}>
      Como o score é calculado
    </div>
    <pre
      className="whitespace-pre-wrap font-sans"
      style={{ margin: 0, fontSize: 11.5, color: '#334155' }}
    >
      {SCORE_FORMULA_LINES.join('\n')}
    </pre>
  </div>
);

interface StageProps {
  stage: FunnelStageScore;
}

/** Card branco de etapa (flat — sem gradiente). */
const Stage: React.FC<StageProps> = ({ stage }) => (
  <div
    className="flex-1 min-w-0 rounded-lg p-3 flex flex-col gap-0.5"
    style={{
      backgroundColor: 'var(--theme-card-background)',
      border: '1px solid var(--theme-card-border)',
    }}
    data-testid={`funnel-stage-${stage.key}`}
  >
    <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
      {stage.label}
    </div>
    <div
      className="text-2xl font-bold"
      style={{ color: 'var(--theme-card-text)', letterSpacing: '-0.02em', lineHeight: 1.1 }}
    >
      {formatNumber(stage.count)}
    </div>
    {stage.score !== null && (
      <span
        className="inline-flex items-center gap-1 mt-1.5 self-start px-2 py-0.5 rounded-full text-[11px] font-semibold"
        style={{ backgroundColor: 'rgba(139, 92, 246, 0.12)', color: '#7c3aed' }}
        data-testid={`funnel-stage-${stage.key}-score`}
      >
        <FiActivity size={10} />
        score {stage.score}
      </span>
    )}
  </div>
);

interface ArrowProps {
  pct: number | null;
}

/** Seta entre etapas — horizontal no desktop, rotaciona (para baixo) no mobile. */
const Arrow: React.FC<ArrowProps> = ({ pct }) => (
  <div
    className="flex lg:flex-col items-center justify-center gap-1 flex-shrink-0 px-1 py-1 lg:min-w-[52px] text-[11px] font-semibold"
    aria-hidden
    style={{ color: 'var(--theme-muted-text)' }}
  >
    <span>{formatPct(pct)}</span>
    <span
      className="inline-block rotate-90 lg:rotate-0"
      style={{ fontSize: 16, lineHeight: 1, color: 'var(--theme-muted-text)' }}
    >
      →
    </span>
  </div>
);

export const FunnelNarrativeCard: React.FC<FunnelNarrativeCardProps> = ({ narrative }) => {
  const [showFormula, setShowFormula] = useState(false);
  const cScore = narrative.consolidatedScore;
  const cDelta = narrative.consolidatedScoreDelta;
  const positive = cDelta > 0.5;
  const negative = cDelta < -0.5;
  const sColor = consolidatedScoreColor(cScore);

  // Layout esperado: 4 stages exatas (visitors, leads, opportunities, won).
  // Defesa: se backend retornar diferente, renderiza vazio com aviso.
  if (narrative.stages.length !== 4) {
    return (
      <div
        className="rounded-xl p-5 text-center"
        style={{
          backgroundColor: 'rgba(139, 92, 246, 0.10)',
          border: '1px solid var(--theme-card-border)',
          color: 'var(--theme-muted-text)',
        }}
        data-testid="funnel-narrative-card-empty"
      >
        Funil narrativo indisponível (esperava 4 etapas, recebeu {narrative.stages.length}).
      </div>
    );
  }

  return (
    <div
      className="rounded-xl p-4"
      style={{
        backgroundColor: 'rgba(139, 92, 246, 0.10)',
        border: '1px solid var(--theme-card-border)',
      }}
      data-testid="funnel-narrative-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h2
            className="text-base font-bold"
            style={{ color: 'var(--theme-card-text)', letterSpacing: '-0.02em' }}
          >
            Jornada do funil
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
            Visitantes → Leads → Propostas → Ganhos · metas 7% / 30% / 13%
          </p>
        </div>
        <div className="relative flex items-center gap-2.5 flex-shrink-0">
          <div className="text-right">
            <div
              className="text-[10px] uppercase tracking-wide font-bold"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Score do funil
            </div>
            <div
              className="text-2xl font-bold inline-flex items-center gap-1.5"
              style={{ color: sColor, lineHeight: 1 }}
              data-testid="funnel-consolidated-score"
            >
              {cScore}
              {(positive || negative) && (
                <span
                  className="text-xs font-semibold inline-flex items-center gap-0.5"
                  style={{ color: positive ? '#10b981' : '#ef4444' }}
                >
                  {positive ? <FiTrendingUp size={11} /> : <FiTrendingDown size={11} />}
                  {Math.abs(cDelta).toFixed(1)}pp
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="flex items-center justify-center flex-shrink-0 rounded-full transition-colors"
            style={{
              width: 26,
              height: 26,
              border: '1px solid var(--theme-card-border)',
              backgroundColor: 'var(--theme-card-background)',
              color: 'var(--theme-muted-text)',
            }}
            onMouseEnter={() => setShowFormula(true)}
            onMouseLeave={() => setShowFormula(false)}
            onFocus={() => setShowFormula(true)}
            onBlur={() => setShowFormula(false)}
            onClick={() => setShowFormula((prev) => !prev)}
            aria-label="Ver fórmula do score"
            data-testid="funnel-score-formula-trigger"
          >
            <FiHelpCircle size={14} />
          </button>
          {showFormula && <ScoreFormulaTooltip />}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-stretch gap-1.5">
        <Stage stage={narrative.stages[0]!} />
        <Arrow pct={narrative.stages[0]!.conversionToNext} />
        <Stage stage={narrative.stages[1]!} />
        <Arrow pct={narrative.stages[1]!.conversionToNext} />
        <Stage stage={narrative.stages[2]!} />
        <Arrow pct={narrative.stages[2]!.conversionToNext} />
        <Stage stage={narrative.stages[3]!} />
      </div>
    </div>
  );
};

FunnelNarrativeCard.displayName = 'FunnelNarrativeCard';
